import React, { useState, useRef } from 'react';
import { MdSearch, MdClear, MdAdd } from 'react-icons/md';

const MultiSelect = ({ 
  options = [], 
  value = [], 
  onChange, 
  placeholder = "Selecionar opções...",
  label = "",
  name = "",
  disabled = false,
  searchable = true,
  maxHeight = "300px",
  allowCreate = false,
  onCreateNew = null,
  createPlaceholder = "Criar nova opção..."
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateOption, setShowCreateOption] = useState(false);
  const [newOptionText, setNewOptionText] = useState('');
  const searchInputRef = useRef(null);
  const createInputRef = useRef(null);

  const filteredOptions = options.filter(option =>
    option && option.label && option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleToggle = (option) => {
    if (disabled) return;
    
    const isSelected = value.some(item => item.value === option.value);
    
    if (isSelected) {
      // Remove from selection
      onChange(value.filter(item => item.value !== option.value));
    } else {
      // Add to selection
      onChange([...value, option]);
    }
  };

  const handleClearAll = () => {
    if (!disabled) {
      onChange([]);
    }
  };

  const handleSelectAll = () => {
    if (!disabled) {
      onChange([...filteredOptions.filter(option => option && option.label)]);
    }
  };

  const getSelectedCount = () => {
    return value.length;
  };

  const getTotalCount = () => {
    return filteredOptions.length;
  };

  // Function to determine if a color is dark
  const isDarkColor = (color) => {
    if (!color) return false;
    
    // Convert rgb() to hex if needed
    let hexColor = color;
    if (color.startsWith('rgb(')) {
      const rgb = color.match(/\d+/g);
      if (rgb && rgb.length === 3) {
        const r = parseInt(rgb[0]);
        const g = parseInt(rgb[1]);
        const b = parseInt(rgb[2]);
        hexColor = `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
      }
    }
    
    // Convert to hex if it starts with #
    if (hexColor.startsWith('#')) {
      const hex = hexColor.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      
      // Calculate luminance
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance < 0.5;
    }
    
    return false;
  };

  const handleCreateNew = () => {
    if (!allowCreate || !onCreateNew || !newOptionText.trim()) return;
    
    const newOption = {
      value: `new_${Date.now()}`, // Temporary ID
      label: newOptionText.trim()
    };
    
    // Call the parent's onCreateNew function
    onCreateNew(newOption);
    
    // Add to current selection
    onChange([...value, newOption]);
    
    // Reset form
    setNewOptionText('');
    setShowCreateOption(false);
    setSearchTerm('');
  };

  const handleCancelCreate = () => {
    setNewOptionText('');
    setShowCreateOption(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreateNew();
    } else if (e.key === 'Escape') {
      handleCancelCreate();
    }
  };


  return (
    <div className="form-group">
      {label && (
        <label className="form-label" style={{ 
          display: 'block', 
          marginBottom: '8px', 
          fontWeight: '500',
          color: '#374151'
        }}>
          {label}
        </label>
      )}
      
      <div style={{ 
        backgroundColor: 'white',
        border: '1px solid #d1d5db',
        borderRadius: '8px',
        padding: '16px',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
      }}>
        {/* Header with search and actions */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '16px',
          gap: '12px'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            flex: 1
          }}>
            {searchable && (
              <div style={{ position: 'relative', flex: 1, maxWidth: '300px' }}>
                <MdSearch 
                  size={20} 
                  style={{ 
                    position: 'absolute', 
                    left: '12px', 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    color: '#6b7280'
                  }} 
                />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Pesquisar..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px 8px 40px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    outline: 'none',
                    backgroundColor: disabled ? '#f9fafb' : 'white'
                  }}
                  disabled={disabled}
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      color: '#6b7280',
                      cursor: 'pointer',
                      padding: '2px'
                    }}
                  >
                    <MdClear size={16} />
                  </button>
                )}
              </div>
            )}
          </div>
          
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            fontSize: '0.875rem',
            color: '#6b7280'
          }}>
            <span>{getSelectedCount()} de {getTotalCount()} selecionadas</span>
            {allowCreate && !disabled && (
              <button
                type="button"
                onClick={() => setShowCreateOption(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#10b981',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#ecfdf5'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                <MdAdd size={14} />
                Criar nova
              </button>
            )}
            {getSelectedCount() > 0 && !disabled && (
              <button
                type="button"
                onClick={handleClearAll}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#ef4444',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  fontWeight: '500',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#fef2f2'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                Limpar tudo
              </button>
            )}
            {getTotalCount() > 0 && getSelectedCount() < getTotalCount() && !disabled && (
              <button
                type="button"
                onClick={handleSelectAll}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#3b82f6',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  fontWeight: '500',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#eff6ff'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                Selecionar tudo
              </button>
            )}
          </div>
        </div>

        {/* Create new option form */}
        {showCreateOption && (
          <div style={{
            backgroundColor: '#f8fafc',
            border: '2px dashed #d1d5db',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '16px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <MdAdd size={20} style={{ color: '#10b981' }} />
              <input
                ref={createInputRef}
                type="text"
                placeholder={createPlaceholder}
                value={newOptionText}
                onChange={(e) => setNewOptionText(e.target.value)}
                onKeyDown={handleKeyPress}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  outline: 'none'
                }}
                autoFocus
              />
              <button
                type="button"
                onClick={handleCreateNew}
                disabled={!newOptionText.trim()}
                style={{
                  padding: '8px 16px',
                  backgroundColor: newOptionText.trim() ? '#10b981' : '#d1d5db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: newOptionText.trim() ? 'pointer' : 'not-allowed',
                  transition: 'background-color 0.2s'
                }}
              >
                Criar
              </button>
              <button
                type="button"
                onClick={handleCancelCreate}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'transparent',
                  color: '#6b7280',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f3f4f6';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        )}

        {/* Toggle grid */}
        <div
          style={{
            maxHeight,
            overflowY: 'auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '8px',
            padding: '4px 0'
          }}
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.filter(option => option && option.label).map((option) => {
              const isSelected = value.some(item => item.value === option.value);
              
              return (
                <div
                  key={option.value}
                  onClick={() => handleToggle(option)}
                  style={{
                    padding: '12px',
                    border: `2px solid ${isSelected ? (option.color || '#3b82f6') : '#e5e7eb'}`,
                    borderRadius: '8px',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    backgroundColor: isSelected ? (option.color || '#eff6ff') : 'white',
                    transition: 'all 0.2s',
                    opacity: disabled ? 0.6 : 1,
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    if (!disabled && !isSelected) {
                      e.target.style.borderColor = option.color || '#3b82f6';
                      e.target.style.backgroundColor = option.color ? `${option.color}08` : '#f8fafc';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!disabled && !isSelected) {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.backgroundColor = 'white';
                    }
                  }}
                >
                  {/* Toggle indicator */}
                  <div
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: isSelected ? '#3b82f6' : '#e5e7eb',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s'
                    }}
                  >
                    {isSelected && (
                      <div
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: 'white'
                        }}
                      />
                    )}
                  </div>
                  
                  {/* Color bar */}
                  {option.color && (
                    <div
                      style={{
                        position: 'absolute',
                        left: '0',
                        top: '0',
                        bottom: '0',
                        width: '4px',
                        backgroundColor: option.color,
                        borderRadius: '8px 0 0 8px'
                      }}
                    />
                  )}
                  
                  {/* Option content */}
                  <div style={{ 
                    paddingRight: '32px',
                    paddingLeft: option.color ? '12px' : '0',
                    minHeight: '40px',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <span style={{ 
                      fontSize: '0.875rem',
                      fontWeight: isSelected ? '500' : '400',
                      color: isSelected && option.color && isDarkColor(option.color) ? '#ffffff' : '#000000',
                      lineHeight: '1.4'
                    }}>
                      {option.label || 'Opção sem nome'}
                    </span>
                  </div>
                </div>
              );
            })
          ) : (
            <div style={{
              gridColumn: '1 / -1',
              padding: '24px',
              textAlign: 'center',
              color: '#6b7280',
              fontSize: '0.875rem'
            }}>
              {searchTerm ? (
                <div>
                  <p>Nenhuma opção encontrada para "{searchTerm}"</p>
                  {allowCreate && !disabled && (
                    <button
                      type="button"
                      onClick={() => {
                        setNewOptionText(searchTerm);
                        setShowCreateOption(true);
                      }}
                      style={{
                        marginTop: '12px',
                        padding: '8px 16px',
                        backgroundColor: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        margin: '12px auto 0',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
                    >
                      <MdAdd size={16} />
                      Criar "{searchTerm}"
                    </button>
                  )}
                </div>
              ) : (
                'Nenhuma opção disponível'
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MultiSelect;
